
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useGeneralActions } from "@/utils/actions";
import { trainingService } from "@/services";
import VueTextarea from "@/global-components/form-elements/textarea/Main.vue";

interface Certificate {
  number: string;
  name_surname: string;
  type: string;
  uuid?: string;
  phone?: string;
  email?: string;
  emailed_at?: string;
}

interface Ability {
  can_update: boolean;
  can_delete: boolean;
}

interface PostTraining {
  number: string;
  title: string;
  description: string;
  date: string;
  template: string;
  certificates: Certificate[];
  abilities?: Ability;
}

export default defineComponent({
  name: "CreateTrainings",
  components: { VueTextarea },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const id = computed(() => route.params.id);
    const headerTitle = computed<string>(() => {
      if (id.value) return `EDIT TRAINING (#${id.value})`;
      return "CREATE TRAINING";
    });

    const templateOptions = ref([
      { label: "Basic Template", value: "certificate-template-3.pdf" },
    ]);

    const certificateTypeOptions = ref([
      { label: "Participation", value: "participation" },
      { label: "Successful Completion", value: "Successful Completion" },
      { label: "Achievement", value: "Achievement" },
      { label: "Appreciation", value: "Appreciation" },
    ]);

    const { fetchRecord, createOrUpdate } = useGeneralActions();
    const { loading, data, fetch } = fetchRecord({
      fetchApiMethod: trainingService.show,
      errorCallback() {
        router.go(-1);
      },
      successCallback() {
        setFormData(data.value);
      },
    });

    const {
      processing,
      formData,
      setFormData,
      validationErrors,
      submit,
    } = createOrUpdate({
      createApiMethod: trainingService.store,
      updateApiMethod: trainingService.update,
      successCallback() {
        router.go(-1);
      },
      params: {
        number: "",
        title: "",
        description: "",
        date: "",
        template: "",
        certificates: [], // Initialize with empty array here
        abilities: {
          can_update: true,
          can_delete: true,
        },
      } as PostTraining,
    });

    const addCertificate = () => {
      // Ensure certificates array exists before pushing
      if (!formData.certificates) {
        formData.certificates = [];
      }
      formData.certificates.push({
        number: "",
        name_surname: "",
        type: "",
        email: "",
        phone: "",
      });
    };

    const removeCertificate = (index: number) => {
      formData.certificates.splice(index, 1);
    };

    watch(id, (val) => fetch(val).finally(), { flush: "post" });
    onMounted(() => {
      if (id.value) {
        fetch(id.value).finally();
      } else {
        // Make sure to initialize certificates as an array if it doesn't exist
        console.log(formData);
        if (!formData.certificates) {
          formData.certificates = [];
        }

        // Add the first certificate if there are none
        if (formData.certificates.length === 0) {
          addCertificate();
        }
      }
    });

    const onSubmit = () => submit(id.value);

    const viewCertificate = (uuid: string) => {
      if (uuid) {
        window.open(`https://certificates.smiic.org/?uuid[]=${uuid}`, "_blank");
      }
    };

    const hasValidCertificates = computed(() => {
      return (
        formData.certificates &&
        formData.certificates.some((cert: Certificate) => cert.uuid)
      );
    });

    const viewAllCertificates = () => {
      const uuids = formData.certificates
        .filter((cert: Certificate) => cert.uuid)
        .map((cert: Certificate) => `uuid[]=${cert.uuid}`)
        .join("&");

      if (uuids) {
        window.open(`https://certificates.smiic.org/?${uuids}`, "_blank");
      }
    };

    return {
      headerTitle,
      loading,
      processing,
      formData,
      validationErrors,
      onSubmit,
      templateOptions,
      certificateTypeOptions,
      addCertificate,
      removeCertificate,
      viewCertificate,
      viewAllCertificates,
      hasValidCertificates,
    };
  },
});
